'use client';

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';

import
  Link
from 'next/link';

import {
  useParams
} from 'next/navigation';

import {
  Skeleton
} from '@repo/ui/skeleton';

import {
  formatMarks,
  paceComparison,
  practiceLevelLabel,
  type PracticeLevel,
  type PracticeSession
} from '@repo/api-client/content-management-service';

import {
  showPrimerFirst
} from '@/app/lib/practice/level-flow';

import {
  levelOf
} from '@/app/lib/practice/levels';

import {
  AttemptSignals
} from '@/app/lib/practice/attempt-signals';

import {
  practicePackInfo
} from '@/app/lib/practice/pack';

import {
  answeredCount,
  firstUnanswered,
  nextUnanswered
} from '@/app/lib/practice/session-view';

import {
  usePracticePack
} from '@/app/lib/practice/use-practice-pack';

import {
  usePracticeSession
} from '@/app/lib/practice/use-practice-session';

import {
  BuyPracticePack
} from '@/app/ui/portal/practice/buy-practice-pack';

import {
  PracticeBottomBar,
  PracticeQuestionView,
  SOLVE_AREA_CLASS,
  SOLVE_AREA_STYLE,
  type PostAttempt,
  type PracticeHelpApi
} from '@/app/ui/portal/practice/practice-question';

import {
  PracticeQuestionDots
} from '@/app/ui/portal/practice/practice-question-dots';

import {
  LevelChooser,
  LevelLockPanel,
  PrimerCard,
  PrimerFold
} from '@/app/ui/portal/practice/practice-levels';

import {
  PracticeVideo,
  videoTitle
} from '@/app/ui/portal/practice/practice-video';

// One topic of the practice pack, one question at a time, inside a SESSION
// the server keeps (feature 0888). The questions, hints and solutions arrive
// from the server already typeset (MathML), and only when this account may
// open the topic.
//
// Every Check and option pick is posted to the portal's attempts route,
// which checks the answer on the server (the MathLive box sends raw LaTeX;
// the server normalises it); the marks, badges and feedback on this page all
// come from the session it returns. The page is never sent an answer: hints,
// the options and the worked solution are fetched (and recorded) only when
// the learner asks for them. The solving layout itself (two panes on wide screens, the maths
// keyboard, figures) is PracticeQuestionView.
//
// Levels (practice-levels-design.md): an open session is continued at its
// own level; otherwise the learner picks Start from scratch, Guided or Exam
// mode first, and that level holds until the session ends. Start from
// scratch opens with the topic primer; Exam mode has no hints or options.

const CARD = 'rounded-2xl border border-gray-100 dark:border-gray-800 bg-white dark:bg-gray-900';

const PILL = 'min-h-[44px] text-xs font-semibold px-4 rounded-full border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:border-[rgba(1,158,250,0.5)] transition-colors disabled:opacity-50 disabled:cursor-default focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[rgb(1,158,250)]';

function SessionSummary({
  session, onRestart, busy, packHref
}: { session: PracticeSession
  onRestart: () => void
  busy: boolean
  packHref: string }) {

  return (
    <div className={`${ CARD } p-5 space-y-3`} role="status">
      <div>
        <p className="text-sm font-semibold text-[#111111] dark:text-white">Session finished</p>
        <p className="text-2xl font-light text-[#111111] dark:text-white mt-1">
          {`${ formatMarks(session.marksEarned) } of ${ session.marksTotal } marks`}
        </p>
        <p className="text-sm text-gray-400 mt-1">
          {`${ session.correctCount } of ${ session.questionsTotal } questions correct.`}
        </p>
        <p className="text-sm text-gray-400 mt-1">{paceComparison(session.activeMs, session.marksTotal)}</p>
        <p className="text-sm text-gray-400 mt-1">{`Level: ${ practiceLevelLabel(session.level) }`}</p>
      </div>
      <div className="flex flex-wrap gap-3">
        <button
          type="button"
          disabled={busy}
          onClick={onRestart}
          className="text-sm font-semibold px-4 py-2 rounded-xl border border-gray-200 dark:border-gray-700 text-[rgba(1,158,250,1)] hover:border-[rgba(1,158,250,0.5)] disabled:opacity-50"
        >
          Practise this topic again
        </button>
        <Link
          href={packHref}
          className="text-sm font-semibold px-4 py-2 rounded-xl border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:border-[rgba(1,158,250,0.5)]"
        >
          All topics
        </Link>
      </div>
    </div>
  );
}

export function PracticeTopicPage({
  packKey
}: { packKey: string }) {

  const info = practicePackInfo(packKey);
  const PACK = info.key;
  const params = useParams<{ topicId: string }>();
  const topicId = typeof params?.topicId === 'string' ? params.topicId : '';

  const {
    data, loading, errorStatus, reload
  } = usePracticePack(PACK, topicId);

  const topic = data?.topic;

  const {
    session, phase, lock, busy, error: sessionError, start, chooseLevel, cancelChoice, attempt, hint, options, solution, reasons, finish
  } = usePracticeSession(PACK, topicId, Boolean(topic));

  const level = levelOf(session);
  // The primer stays first at Start from scratch until the learner moves on.
  const [ primerDoneFor, setPrimerDoneFor ] = useState<string | null>(null);

  const [ current, setCurrent ] = useState(0);

  const goTo = useCallback((i: number) => {

    setCurrent((n) => (i < 0 || i >= (topic?.questions.length ?? 0) ? n : i));
    window.scrollTo({
      top: 0,
      behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth'
    });
  }, [ topic?.questions.length ]);

  const question = topic?.questions[current];
  const sessionOpen = session?.status === 'IN_PROGRESS';
  const questionIds = useMemo(() => topic?.questions.map((q) => q.id) ?? [], [ topic ]);

  // Continue (an open session arriving, from the topic list or the level
  // lock) opens the first question not yet correct, revealed or skipped,
  // once per session: later updates to the same session never move the
  // learner. Adjusted during render, React's pattern for state that follows
  // a prop.
  const [ positionedFor, setPositionedFor ] = useState<string | null>(null);

  if (topic && session && sessionOpen && positionedFor !== session.sessionUuid) {

    setPositionedFor(session.sessionUuid);
    setCurrent(firstUnanswered(questionIds, session));
  }

  // ── Quiet signals: time on screen, tab-aways, pastes ──────────────────────
  const signalsRef = useRef<AttemptSignals | null>(null);

  useEffect(() => {

    const signals = new AttemptSignals(() => Date.now(), document.visibilityState === 'visible');
    signalsRef.current = signals;

    const onVisibility = () => {

      if (document.visibilityState === 'hidden') {

        signals.hide();
      } else {

        signals.show();
      }
    };

    document.addEventListener('visibilitychange', onVisibility);

    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, []);

  const questionId = question?.id;

  useEffect(() => {

    if (questionId) {

      signalsRef.current?.focus(questionId);
    }
  }, [ questionId ]);

  const signalsFor = useCallback((id: string) => signalsRef.current?.snapshot(id) ?? {
    timeOnQuestionMs: 0,
    pasted: false,
    tabAwayCount: 0,
    tabAwayMs: 0,
    activeMsDelta: 0
  }, []);

  const onAttempt = useCallback<PostAttempt>(async (id, kind, extra) => attempt({
    questionId: id,
    kind,
    ...(extra ?? {}),
    ...signalsFor(id)
  }), [ attempt, signalsFor ]);

  const help = useMemo<PracticeHelpApi>(() => ({
    hint: (id, index) => hint({
      questionId: id,
      index,
      ...signalsFor(id)
    }),
    options: (id) => options({
      questionId: id,
      ...signalsFor(id)
    }),
    solution: (id) => solution({
      questionId: id,
      ...signalsFor(id)
    }),
    reasons: (id) => reasons({
      questionId: id,
      ...signalsFor(id)
    })
  }), [ hint, options, solution, reasons, signalsFor ]);

  const onPaste = useCallback(() => {

    if (questionId) {

      signalsRef.current?.paste(questionId);
    }
  }, [ questionId ]);

  // Restart and "Practise this topic again" go back to the level choice.
  const restart = useCallback(() => {

    chooseLevel();
  }, [ chooseLevel ]);

  const startAt = useCallback((chosen: PracticeLevel) => {

    if (sessionOpen && answeredCount(session) > 0
      && !window.confirm('Start this topic again from zero? Your current session stays in your history.')) {

      return;
    }

    setCurrent(0);
    void start(chosen, sessionOpen);
  }, [ session, sessionOpen, start ]);

  const finishSession = useCallback(() => {

    if (!window.confirm('Finish this session? Questions you have not answered will not earn marks.')) {

      return;
    }

    void finish();
  }, [ finish ]);

  const answered = answeredCount(session);

  return (
    <div className="space-y-6">

      <div>
        <div className="flex flex-wrap items-center gap-2 text-xs text-gray-400">
          <Link href="/student/tools" className="hover:text-gray-500 transition-colors">Study Tools</Link>
          <span>/</span>
          <Link href={info.href} className="hover:text-gray-500 transition-colors">{info.title}</Link>
        </div>
        <h1 className="text-2xl font-light text-[#111111] dark:text-white mt-1">
          {topic?.title ?? data?.topics.find((t) => t.id === topicId)?.title ?? 'Practice'}
        </h1>
        <p className="text-xs text-gray-400 mt-0.5">{info.name}</p>
        {session && topic && phase === 'ready' && (
          <p className="text-sm text-gray-400 mt-1">
            {sessionOpen
              ? `${ practiceLevelLabel(level) } · ${ answered } of ${ session.questionsTotal } answered · ${ formatMarks(session.marksEarned) } of ${ session.marksTotal } marks · saved to your account`
              : `${ practiceLevelLabel(level) } · ${ formatMarks(session.marksEarned) } of ${ session.marksTotal } marks`}
          </p>
        )}
      </div>

      {loading && !data && (
        <div className="space-y-3">
          <Skeleton className="h-8 w-1/2 rounded-xl" />
          <Skeleton className="h-64 w-full rounded-2xl" />
        </div>
      )}

      {!loading && errorStatus === 403 && (
        <div className={`${ CARD } p-5 space-y-3`}>
          <div>
            <p className="text-sm font-semibold text-[#111111] dark:text-white">{`This topic is part of the full ${ info.name } pack`}</p>
            <p className="text-sm text-gray-400 mt-1">
              {`Buy the ${ info.title } to open every topic, with hints and worked solutions.`}
            </p>
          </div>
          {data?.entitlementUnknown ? (
            <button type="button" onClick={reload} className="text-sm font-semibold text-[rgba(1,158,250,1)] hover:underline">
              We could not check your access. Check again
            </button>
          ) : (
            <BuyPracticePack packKey={PACK} label={`Buy ${ info.name }`} onAlreadyOwned={reload} />
          )}
        </div>
      )}

      {!loading && errorStatus === 404 && (
        <div className={`${ CARD } p-5`}>
          <p className="text-sm text-gray-400">That topic is not available yet.</p>
          <Link href={info.href} className="inline-block mt-3 text-sm font-semibold text-[rgba(1,158,250,1)] hover:underline">
            Back to the topics →
          </Link>
        </div>
      )}

      {!loading && errorStatus !== null && errorStatus !== 403 && errorStatus !== 404 && (
        <div className={`${ CARD } p-5`}>
          <p className="text-sm text-gray-400">
            {errorStatus === 401 ? 'Please sign in again.' : 'This topic did not load. Check your connection and try again.'}
          </p>
          <button type="button" onClick={reload} className="mt-3 text-sm font-semibold text-[rgba(1,158,250,1)] hover:underline">
            Try again
          </button>
        </div>
      )}

      {topic && topic.videos.length > 0 && (
        <div className="flex flex-wrap gap-4">
          {topic.videos.map((id) => (
            <PracticeVideo key={id} videoId={id} title={videoTitle(id)} />
          ))}
        </div>
      )}

      {topic && sessionError && phase !== 'ready' && (
        <p className="text-sm text-red-500" role="alert">{sessionError}</p>
      )}

      {topic && (phase === 'loading' || (phase === 'ready' && !session)) && (
        <Skeleton className="h-64 w-full rounded-2xl" />
      )}

      {topic && phase === 'choose' && (
        <LevelChooser
          key={session?.sessionUuid ?? 'new'}
          initial={session ? level : 'guided'}
          restarting={sessionOpen}
          busy={busy}
          onStart={startAt}
          onCancel={session ? cancelChoice : undefined}
        />
      )}

      {topic && phase === 'locked' && lock && (
        <LevelLockPanel
          lock={lock}
          busy={busy}
          onContinue={() => void start(lock.lockedLevel, false)}
          onRestart={() => {

            setCurrent(0);
            void start(lock.requestedLevel, true);
          }}
        />
      )}

      {topic && phase === 'ready' && session && !sessionOpen && (
        <SessionSummary session={session} onRestart={restart} busy={busy} packHref={info.href} />
      )}

      {topic && phase === 'ready' && session && sessionOpen && topic.primer
        && showPrimerFirst(level, answered, primerDoneFor === session.sessionUuid, true) && (
        <PrimerCard primer={topic.primer} onBegin={() => setPrimerDoneFor(session.sessionUuid)} />
      )}

      {topic && phase === 'ready' && session && sessionOpen && question
        && !showPrimerFirst(level, answered, primerDoneFor === session.sessionUuid, Boolean(topic.primer)) && (
        <div className={SOLVE_AREA_CLASS} style={SOLVE_AREA_STYLE}>
          <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
            <PracticeQuestionDots
              questions={topic.questions}
              states={session.questions}
              current={current}
              onGo={goTo}
            />
            <div className="flex gap-2">
              <button type="button" disabled={busy} onClick={restart} className={PILL}>
                Restart
              </button>
              <button type="button" disabled={busy} onClick={finishSession} className={PILL}>
                Finish session
              </button>
            </div>
          </div>

          {sessionError && (
            <p className="text-sm text-red-500 mb-3" role="alert">{sessionError}</p>
          )}

          {level === 'scratch' && topic.primer && <PrimerFold primer={topic.primer} />}

          <PracticeQuestionView
            key={`${ session.sessionUuid }-${ question.id }`}
            question={question}
            index={current}
            total={topic.questions.length}
            topicLabel={`${ info.paperLabel } · ${ topic.title }`}
            reasonList={topic.reasonList}
            level={level}
            state={session.questions[question.id]}
            onAttempt={onAttempt}
            help={help}
            onPaste={onPaste}
            onSkipped={() => {

              // Skip moves on to the next question still to do (it stays
              // recorded as skipped); with none left, stay here.
              const next = nextUnanswered(questionIds, session, current);

              if (next !== null) {

                goTo(next);
              }
            }}
          />

          <p className="text-xs text-gray-400 mt-6">{topic.source}</p>

          <PracticeBottomBar
            score={`Score ${ formatMarks(session.marksEarned) } / ${ session.marksTotal }`}
            canPrevious={current > 0}
            onPrevious={() => goTo(current - 1)}
            nextLabel={current < topic.questions.length - 1 ? 'Next' : 'Finish'}
            nextDisabled={busy}
            onNext={() => (current < topic.questions.length - 1 ? goTo(current + 1) : finishSession())}
          />
        </div>
      )}
    </div>
  );
}
